import { User } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { renameCompetition, updateFPConfig, AdminCompetition } from "../../../api/admin";
import { card, primaryBtn, inputStyle, badge, errorStyle } from "../../../styles";
import { COMPETITION_MODES } from "../../../lib/admin/competitionModes";
import SyndicateTab from "./SyndicateTab";

interface Props {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
}

export default function ModesTab({ user, comp, reload }: Props) {
  const { t } = useTranslation();
  const [modesDraft, setModesDraft] = useState<string[]>(comp.supportedModes);
  const [capsDraft, setCapsDraft] = useState<Record<string, number | null>>(comp.modeJoinCaps);

  const [fpTopTier, setFpTopTier] = useState<string>(comp.config?.topTierSize != null ? String(comp.config.topTierSize) : "");
  const [fpRelegation, setFpRelegation] = useState<string>(comp.config?.relegationSize != null ? String(comp.config.relegationSize) : "");
  const [fpChampion, setFpChampion] = useState<boolean>(comp.config?.championEnabled ?? true);
  const [fpTopTierLabel, setFpTopTierLabel] = useState<string>(comp.config?.topTierLabel ?? "");
  const [fpRelegationLabel, setFpRelegationLabel] = useState<string>(comp.config?.relegationLabel ?? "");

  const [savingMode, setSavingMode] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});

  const realGws = comp.gameweeks.filter((gw) => gw.number > 0);

  const persistModesAndCaps = async (nextModes: string[], nextCaps: Record<string, number | null>) => {
    await renameCompetition(user, comp.id, comp.name, nextModes, nextCaps);
    await reload();
  };

  const handleToggle = async (mode: string, checked: boolean) => {
    const nextModes = checked ? [...modesDraft, mode] : modesDraft.filter((m) => m !== mode);
    const prevModes = modesDraft;
    setModesDraft(nextModes);
    setSavingMode(mode);
    setCardErrors((prev) => ({ ...prev, [mode]: "" }));
    try {
      await persistModesAndCaps(nextModes, capsDraft);
    } catch {
      setModesDraft(prevModes);
      setCardErrors((prev) => ({ ...prev, [mode]: t("admin.errors.failedToSave") }));
    } finally {
      setSavingMode(null);
    }
  };

  const handleCapChange = (mode: string, value: string) => {
    setCapsDraft((prev) => ({ ...prev, [mode]: value === "" ? null : parseInt(value, 10) }));
  };

  const handleSaveCard = async (mode: string) => {
    setSavingMode(mode);
    setCardErrors((prev) => ({ ...prev, [mode]: "" }));
    try {
      if (mode === "FINAL_POSITIONS") {
        await updateFPConfig(user, comp.id, {
          topTierSize: fpTopTier === "" ? null : parseInt(fpTopTier, 10),
          relegationSize: fpRelegation === "" ? null : parseInt(fpRelegation, 10),
          championEnabled: fpChampion,
          topTierLabel: fpTopTierLabel.trim() || null,
          relegationLabel: fpRelegationLabel.trim() || null,
        });
      }
      await persistModesAndCaps(modesDraft, capsDraft);
    } catch {
      setCardErrors((prev) => ({ ...prev, [mode]: t("admin.errors.failedToSave") }));
    } finally {
      setSavingMode(null);
    }
  };

  const statusFor = (mode: string): { configured: boolean; reason: string } => {
    switch (mode) {
      case "TARGET":
      case "GAMEWEEK_CHALLENGE":
        return comp.hasActiveSquadPlayers
          ? { configured: true, reason: "" }
          : { configured: false, reason: t("admin.modes.needsSetup.noActivePlayers") };
      case "FINAL_POSITIONS": {
        const top = fpTopTier === "" ? null : parseInt(fpTopTier, 10);
        const releg = fpRelegation === "" ? null : parseInt(fpRelegation, 10);
        if (top == null || releg == null) return { configured: false, reason: t("admin.modes.needsSetup.noTierSizes") };
        if (top + releg > comp.clubs.length) return { configured: false, reason: t("admin.modes.needsSetup.tierSizesExceedClubs") };
        return { configured: true, reason: "" };
      }
      case "SCORE_PREDICTOR":
        return comp.hasFixtures
          ? { configured: true, reason: "" }
          : { configured: false, reason: t("admin.modes.needsSetup.noFixtures") };
      case "SYNDICATE":
        if (comp.clubs.length === 0) return { configured: false, reason: t("admin.modes.needsSetup.noClubs") };
        if (comp.syndicatePotCount === 0) return { configured: false, reason: t("admin.modes.needsSetup.noPots") };
        return { configured: true, reason: "" };
      default:
        return { configured: true, reason: "" };
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {COMPETITION_MODES.map(({ key, label }) => {
        const enabled = modesDraft.includes(key);
        const { configured, reason } = statusFor(key);
        const saving = savingMode === key;
        return (
          <div key={key} style={card}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", marginBottom: enabled ? "1rem" : 0 }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.6rem", fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={saving}
                  onChange={(e) => handleToggle(key, e.target.checked)}
                  style={{ width: "1.1rem", height: "1.1rem", flexShrink: 0 }}
                />
                <span style={{ color: "var(--color-text)" }}>{label}</span>
              </label>
              {enabled && (
                configured
                  ? <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)" }}>{t("admin.modes.configured")}</span>
                  : <span style={{ ...badge, background: "var(--color-surface-alt)", color: "var(--color-warning)" }}>⚠ {t("admin.modes.needsSetupLabel")} — {reason}</span>
              )}
            </div>

            {cardErrors[key] && <p style={{ ...errorStyle, marginTop: "0.75rem" }}>{cardErrors[key]}</p>}

            {enabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem" }}>
                {key === "FINAL_POSITIONS" && (
                  <div style={{ padding: "0.75rem", background: "var(--color-surface-raised)", borderRadius: 6, border: "1px solid var(--color-border-subtle)", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--color-text-dim)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {t("admin.fpScoring.sectionTitle")}
                    </p>
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{t("admin.fpScoring.description")}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <label style={{ fontSize: "0.85rem", minWidth: "130px" }}>{t("admin.fpScoring.topTierLabel")}</label>
                      <input type="number" min={0} style={{ ...inputStyle, width: "80px" }} placeholder={t("admin.fpScoring.topTierPlaceholder")} value={fpTopTier} onChange={(e) => setFpTopTier(e.target.value)} />
                      <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{t("admin.fpScoring.topTierHint")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <label style={{ fontSize: "0.85rem", minWidth: "130px" }}>Top tier name</label>
                      <input type="text" maxLength={40} style={{ ...inputStyle, width: "200px" }} placeholder="e.g. Champions League" value={fpTopTierLabel} onChange={(e) => setFpTopTierLabel(e.target.value)} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <label style={{ fontSize: "0.85rem", minWidth: "130px" }}>{t("admin.fpScoring.relegationLabel")}</label>
                      <input type="number" min={0} style={{ ...inputStyle, width: "80px" }} placeholder={t("admin.fpScoring.relegationPlaceholder")} value={fpRelegation} onChange={(e) => setFpRelegation(e.target.value)} />
                      <span style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>{t("admin.fpScoring.relegationHint")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <label style={{ fontSize: "0.85rem", minWidth: "130px" }}>Bottom tier name</label>
                      <input type="text" maxLength={40} style={{ ...inputStyle, width: "200px" }} placeholder="e.g. Relegation Zone" value={fpRelegationLabel} onChange={(e) => setFpRelegationLabel(e.target.value)} />
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", cursor: "pointer" }}>
                      <input type="checkbox" checked={fpChampion} onChange={(e) => setFpChampion(e.target.checked)} />
                      {t("admin.fpScoring.championLabel")}
                    </label>
                  </div>
                )}

                {key === "SYNDICATE" && (
                  <div style={{ padding: "0.75rem", background: "var(--color-surface-raised)", borderRadius: 6, border: "1px solid var(--color-border-subtle)" }}>
                    <SyndicateTab user={user} comp={comp} embedded />
                  </div>
                )}

                {realGws.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                    <label style={{ fontSize: "0.85rem", minWidth: "150px" }}>{t("admin.modes.lastJoinableGw")}</label>
                    <select
                      aria-label={`Last gameweek for new ${label} leagues`}
                      value={capsDraft[key] ?? ""}
                      onChange={(e) => handleCapChange(key, e.target.value)}
                      style={{ ...inputStyle, maxWidth: "220px" }}
                    >
                      <option value="">{t("admin.modes.noCap")}</option>
                      {realGws.map((gw) => (
                        <option key={gw.number} value={gw.number}>{gw.name} (GW{gw.number})</option>
                      ))}
                    </select>
                  </div>
                )}

                <button style={{ ...primaryBtn, alignSelf: "flex-start" }} onClick={() => handleSaveCard(key)} disabled={saving}>
                  {saving ? t("common.saving") : t("common.save")}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
